import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Copy,
  ExternalLink,
  Home,
  MoreVertical,
  Package,
  Palette,
  Pencil,
  Plus,
  ShoppingCart,
  Tags,
  Trash2,
  Users,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_STATUSES = [
  { value: 'concept', label: 'Concept', className: 'bg-slate-100 text-slate-600' },
  { value: 'gepubliceerd', label: 'Gepubliceerd', className: 'bg-emerald-100 text-emerald-700' },
  { value: 'gearchiveerd', label: 'Gearchiveerd', className: 'bg-amber-100 text-amber-700' },
];

const ORDER_STATUSES = [
  { value: 'nieuw', label: 'Nieuw', className: 'bg-amber-100 text-amber-700' },
  { value: 'betaald', label: 'Betaald', className: 'bg-emerald-100 text-emerald-700' },
  { value: 'verzonden', label: 'Verzonden', className: 'bg-blue-100 text-blue-700' },
  { value: 'afgeleverd', label: 'Afgeleverd', className: 'bg-teal-100 text-teal-700' },
  { value: 'geannuleerd', label: 'Geannuleerd', className: 'bg-red-100 text-red-700' },
  { value: 'terugbetaald', label: 'Terugbetaald', className: 'bg-slate-200 text-slate-600' },
];

const EMPTY_PRODUCT_FORM = {
  title: '',
  slug: '',
  sku: '',
  category_id: '',
  short_description: '',
  description: '',
  image_url: '',
  price: '',
  sale_price: '',
  vat_rate: '21',
  track_stock: false,
  stock: '',
  tags: '',
};

const EMPTY_CATEGORY_FORM = { name: '', description: '' };

const getLabel = (options, value) => options.find((option) => option.value === value)?.label || value;
const getClassName = (options, value) => options.find((option) => option.value === value)?.className || 'bg-slate-100 text-slate-700';
const toNumberOrNull = (value) => (value === '' || value === null || value === undefined ? null : Number(value));
const slugify = (value) => value
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '');

const CommercePage = () => {
  const { user, hasCapability } = useAuth();
  const canManage = hasCapability('commerce_manage');
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT_FORM);
  const [slugTouched, setSlugTouched] = useState(false);

  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState(EMPTY_CATEGORY_FORM);

  const [orgSlug, setOrgSlug] = useState('');
  const [orgSlugInput, setOrgSlugInput] = useState('');
  const [savingSlug, setSavingSlug] = useState(false);

  const shopUrl = `${window.location.origin}/${orgSlug || `shop/${user?.organization_id}`}/shop`;

  useEffect(() => {
    if (!canManage) return;
    fetchAll();
  }, [canManage]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [productsRes, categoriesRes, ordersRes, brandingRes, customersRes] = await Promise.all([
        axios.get(`${API}/commerce/products`),
        axios.get(`${API}/commerce/categories`),
        axios.get(`${API}/commerce/orders`),
        axios.get(`${API}/organization/branding`),
        axios.get(`${API}/commerce/customers`),
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setOrders(ordersRes.data);
      setOrgSlug(brandingRes.data?.slug || '');
      setOrgSlugInput(brandingRes.data?.slug || '');
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Kon commerce-gegevens niet laden');
    } finally {
      setLoading(false);
    }
  };

  const saveOrgSlug = async () => {
    try {
      setSavingSlug(true);
      const response = await axios.put(`${API}/organization/branding`, { slug: orgSlugInput });
      setOrgSlug(response.data?.slug || '');
      setOrgSlugInput(response.data?.slug || '');
      toast.success('Webshop-URL opgeslagen');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSavingSlug(false);
    }
  };

  const categoryName = (id) => categories.find((c) => c.id === id)?.name || '—';

  const stats = useMemo(() => ({
    products: products.length,
    published: products.filter((p) => p.status === 'gepubliceerd').length,
    openOrders: orders.filter((o) => ['nieuw', 'betaald'].includes(o.status)).length,
    revenue: orders.filter((o) => ['betaald', 'verzonden', 'afgeleverd'].includes(o.status))
      .reduce((sum, o) => sum + (o.total_amount || 0), 0),
  }), [products, orders]);

  const copyShopUrl = () => {
    navigator.clipboard.writeText(shopUrl);
    toast.success('Winkellink gekopieerd');
  };

  // ---------- Product CRUD ----------
  const openCreateProduct = () => {
    setEditingProduct(null);
    setProductForm(EMPTY_PRODUCT_FORM);
    setSlugTouched(false);
    setProductDialogOpen(true);
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setSlugTouched(true);
    setProductForm({
      title: product.title || '',
      slug: product.slug || '',
      sku: product.sku || '',
      category_id: product.category_id || '',
      short_description: product.short_description || '',
      description: product.description || '',
      image_url: product.image_url || '',
      price: String(product.price ?? ''),
      sale_price: product.sale_price ?? '' ? String(product.sale_price) : '',
      vat_rate: String(product.vat_rate ?? 21),
      track_stock: !!product.track_stock,
      stock: product.stock ?? '' ? String(product.stock) : '',
      tags: (product.tags || []).join(', '),
    });
    setProductDialogOpen(true);
  };

  const handleTitleChange = (value) => {
    setProductForm((prev) => ({
      ...prev,
      title: value,
      slug: slugTouched ? prev.slug : slugify(value),
    }));
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: productForm.title,
        slug: productForm.slug || slugify(productForm.title),
        sku: productForm.sku || null,
        category_id: productForm.category_id || null,
        short_description: productForm.short_description || null,
        description: productForm.description || null,
        image_url: productForm.image_url || null,
        price: Number(productForm.price || 0),
        sale_price: toNumberOrNull(productForm.sale_price),
        vat_rate: Number(productForm.vat_rate || 21),
        track_stock: productForm.track_stock,
        stock: toNumberOrNull(productForm.stock),
        tags: productForm.tags ? productForm.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      };

      if (editingProduct) {
        await axios.put(`${API}/commerce/products/${editingProduct.id}`, payload);
        toast.success('Product bijgewerkt');
      } else {
        await axios.post(`${API}/commerce/products`, payload);
        toast.success('Product toegevoegd');
      }
      setProductDialogOpen(false);
      fetchAll();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Opslaan mislukt');
    }
  };

  const deleteProduct = async (product) => {
    if (!window.confirm(`Product "${product.title}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/commerce/products/${product.id}`);
      toast.success('Product verwijderd');
      fetchAll();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const updateProductStatus = async (product, status) => {
    try {
      await axios.put(`${API}/commerce/products/${product.id}`, { status });
      toast.success('Status bijgewerkt');
      fetchAll();
    } catch (error) {
      toast.error('Status wijzigen mislukt');
    }
  };

  // ---------- Category CRUD ----------
  const openCreateCategory = () => {
    setEditingCategory(null);
    setCategoryForm(EMPTY_CATEGORY_FORM);
    setCategoryDialogOpen(true);
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({ name: category.name || '', description: category.description || '' });
    setCategoryDialogOpen(true);
  };

  const saveCategory = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: categoryForm.name, description: categoryForm.description || null };
      if (editingCategory) {
        await axios.put(`${API}/commerce/categories/${editingCategory.id}`, payload);
        toast.success('Categorie bijgewerkt');
      } else {
        await axios.post(`${API}/commerce/categories`, payload);
        toast.success('Categorie toegevoegd');
      }
      setCategoryDialogOpen(false);
      fetchAll();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Opslaan mislukt');
    }
  };

  const deleteCategory = async (category) => {
    if (!window.confirm(`Categorie "${category.name}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/commerce/categories/${category.id}`);
      toast.success('Categorie verwijderd');
      fetchAll();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  // ---------- Orders ----------
  const updateOrderStatus = async (order, status) => {
    try {
      await axios.put(`${API}/commerce/orders/${order.id}`, { status });
      toast.success('Orderstatus bijgewerkt');
      fetchAll();
    } catch (error) {
      toast.error('Status wijzigen mislukt');
    }
  };

  const customerStats = (customerId) => {
    const customerOrders = orders.filter((o) => o.customer_account_id === customerId);
    return {
      count: customerOrders.length,
      total: customerOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0),
    };
  };

  const toggleCustomerActive = async (customer) => {
    try {
      await axios.put(`${API}/commerce/customers/${customer.id}`, { is_active: !customer.is_active });
      toast.success(customer.is_active ? 'Account gedeactiveerd' : 'Account geactiveerd');
      fetchAll();
    } catch (error) {
      toast.error('Bijwerken mislukt');
    }
  };

  if (!canManage) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 text-sm text-slate-500">
              Je hebt geen toegang tot Commerce.
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Commerce</h1>
            <p className="text-slate-500 text-sm">Producten, categorieën en bestellingen</p>
          </div>
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-2 flex-wrap">
            <Input value={shopUrl} readOnly className="rounded-xl bg-slate-50 font-mono text-sm flex-1 min-w-[240px]" />
            <Button variant="outline" onClick={copyShopUrl} className="rounded-xl shrink-0">
              <Copy className="w-4 h-4 mr-2" />
              Kopiëren
            </Button>
            <a href={shopUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 shrink-0">
              Bekijk winkel
              <ExternalLink className="w-4 h-4" />
            </a>
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Producten</p>
              <p className="text-2xl font-semibold text-slate-900">{stats.products}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Gepubliceerd</p>
              <p className="text-2xl font-semibold text-emerald-600">{stats.published}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Openstaande orders</p>
              <p className="text-2xl font-semibold text-amber-600">{stats.openOrders}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Omzet (betaald+)</p>
              <p className="text-2xl font-semibold text-slate-900">€ {stats.revenue.toFixed(2)}</p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto">
            <TabsList>
              <TabsTrigger value="products"><Package className="w-4 h-4 mr-2" />Producten</TabsTrigger>
              <TabsTrigger value="categories"><Tags className="w-4 h-4 mr-2" />Categorieën</TabsTrigger>
              <TabsTrigger value="orders"><ShoppingCart className="w-4 h-4 mr-2" />Bestellingen</TabsTrigger>
              <TabsTrigger value="customers"><Users className="w-4 h-4 mr-2" />Klanten</TabsTrigger>
              <TabsTrigger value="homepage"><Home className="w-4 h-4 mr-2" />Homepage</TabsTrigger>
            </TabsList>
          </div>

          {/* PRODUCTEN */}
          <TabsContent value="products" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={openCreateProduct} className="rounded-xl bg-teal-600 hover:bg-teal-700">
                <Plus className="w-4 h-4 mr-2" />
                Nieuw product
              </Button>
            </div>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-6 text-sm text-slate-500">Laden...</div>
                ) : products.length === 0 ? (
                  <div className="p-6 text-sm text-slate-500">Nog geen producten toegevoegd.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {products.map((product) => (
                      <div key={product.id} className="flex items-center gap-4 p-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{product.title}</span>
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${getClassName(PRODUCT_STATUSES, product.status)}`}>
                              {getLabel(PRODUCT_STATUSES, product.status)}
                            </span>
                          </div>
                          <div className="text-sm text-slate-500 mt-0.5">
                            {categoryName(product.category_id)} · € {(product.sale_price ?? product.price).toFixed(2)}
                            {product.sale_price ? ` (was € ${product.price.toFixed(2)})` : ''}
                            {product.track_stock ? ` · voorraad: ${product.stock ?? 0}` : ''}
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditProduct(product)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Bewerken
                            </DropdownMenuItem>
                            {PRODUCT_STATUSES.filter((s) => s.value !== product.status).map((s) => (
                              <DropdownMenuItem key={s.value} onClick={() => updateProductStatus(product, s.value)}>
                                Zet op "{s.label}"
                              </DropdownMenuItem>
                            ))}
                            <DropdownMenuItem onClick={() => deleteProduct(product)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Verwijderen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* CATEGORIEEN */}
          <TabsContent value="categories" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={openCreateCategory} className="rounded-xl bg-teal-600 hover:bg-teal-700">
                <Plus className="w-4 h-4 mr-2" />
                Nieuwe categorie
              </Button>
            </div>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                {categories.length === 0 ? (
                  <div className="p-6 text-sm text-slate-500">Nog geen categorieën toegevoegd.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {categories.map((category) => (
                      <div key={category.id} className="flex items-center gap-4 p-4">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-900">{category.name}</div>
                          {category.description && <div className="text-sm text-slate-500">{category.description}</div>}
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEditCategory(category)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteCategory(category)} className="text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* BESTELLINGEN */}
          <TabsContent value="orders" className="space-y-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                {orders.length === 0 ? (
                  <div className="p-6 text-sm text-slate-500">Nog geen bestellingen.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {orders.map((order) => (
                      <div key={order.id} className="flex items-center gap-4 p-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{order.order_number}</span>
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${getClassName(ORDER_STATUSES, order.status)}`}>
                              {getLabel(ORDER_STATUSES, order.status)}
                            </span>
                          </div>
                          <div className="text-sm text-slate-500 mt-0.5">
                            {order.customer_name} · {order.customer_email} · € {order.total_amount.toFixed(2)}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {order.items.map((item) => `${item.title} x${item.quantity}`).join(', ')}
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {ORDER_STATUSES.filter((s) => s.value !== order.status).map((s) => (
                              <DropdownMenuItem key={s.value} onClick={() => updateOrderStatus(order, s.value)}>
                                Zet op "{s.label}"
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* KLANTEN */}
          <TabsContent value="customers" className="space-y-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                {customers.length === 0 ? (
                  <div className="p-6 text-sm text-slate-500">Nog geen klantaccounts geregistreerd via de webshop.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {customers.map((customer) => {
                      const stats = customerStats(customer.id);
                      return (
                        <div key={customer.id} className="flex items-center gap-4 p-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-900">{customer.name}</span>
                              {!customer.is_active && (
                                <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-slate-200 text-slate-600">Gedeactiveerd</span>
                              )}
                            </div>
                            <div className="text-sm text-slate-500 mt-0.5">
                              {customer.email}{customer.phone ? ` · ${customer.phone}` : ''}
                            </div>
                            <div className="text-xs text-slate-400 mt-0.5">
                              {stats.count} bestelling{stats.count === 1 ? '' : 'en'} · € {stats.total.toFixed(2)} besteed
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-xl"
                            onClick={() => toggleCustomerActive(customer)}
                          >
                            {customer.is_active ? 'Deactiveren' : 'Activeren'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* HOMEPAGE */}
          <TabsContent value="homepage" className="space-y-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Webshop URL</h3>
                  <p className="text-sm text-slate-500">Kies een nette, herkenbare link voor je webshop.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="org-slug">URL-slug</Label>
                    <Input
                      id="org-slug"
                      value={orgSlugInput}
                      onChange={(e) => setOrgSlugInput(e.target.value)}
                      placeholder="life-winterswijk"
                    />
                  </div>
                  <div>
                    <Label>Publieke webshoplink</Label>
                    <div className="flex gap-2">
                      <Input value={shopUrl} readOnly className="rounded-xl bg-slate-50 font-mono text-sm" />
                      <Button variant="outline" onClick={() => { navigator.clipboard.writeText(shopUrl); toast.success('Webshoplink gekopieerd'); }} className="rounded-xl shrink-0">
                        <Copy className="w-4 h-4 mr-2" />
                        Kopiëren
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    className="rounded-xl bg-teal-600 hover:bg-teal-700"
                    disabled={savingSlug || orgSlugInput === orgSlug}
                    onClick={saveOrgSlug}
                  >
                    Webshop-URL opslaan
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Webshop-vormgeving</h3>
                  <p className="text-sm text-slate-500">Hero, secties en indeling van je webshop-homepage bewerk je voortaan in de nieuwe vormgeving-editor.</p>
                </div>
                <Button className="rounded-xl bg-teal-600 hover:bg-teal-700" onClick={() => navigate('/commerce/webshop/vormgeving')}>
                  <Palette className="w-4 h-4 mr-2" />
                  Vormgeving openen
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Product dialog */}
        <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Product bewerken' : 'Nieuw product'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={saveProduct} className="space-y-4">
              <div>
                <Label htmlFor="product-title">Titel</Label>
                <Input id="product-title" required value={productForm.title} onChange={(e) => handleTitleChange(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="product-slug">Slug</Label>
                  <Input
                    id="product-slug"
                    required
                    value={productForm.slug}
                    onChange={(e) => { setSlugTouched(true); setProductForm({ ...productForm, slug: slugify(e.target.value) }); }}
                  />
                </div>
                <div>
                  <Label htmlFor="product-sku">SKU</Label>
                  <Input id="product-sku" value={productForm.sku} onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Categorie</Label>
                <Select value={productForm.category_id || undefined} onValueChange={(value) => setProductForm({ ...productForm, category_id: value })}>
                  <SelectTrigger><SelectValue placeholder="Kies categorie" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="product-price">Prijs (incl. btw)</Label>
                  <Input id="product-price" type="number" step="0.01" required value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="product-sale-price">Kortingsprijs</Label>
                  <Input id="product-sale-price" type="number" step="0.01" value={productForm.sale_price} onChange={(e) => setProductForm({ ...productForm, sale_price: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="product-vat">BTW %</Label>
                  <Input id="product-vat" type="number" step="1" value={productForm.vat_rate} onChange={(e) => setProductForm({ ...productForm, vat_rate: e.target.value })} />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
                <div>
                  <p className="text-sm font-medium text-slate-900">Voorraad bijhouden</p>
                  <p className="text-xs text-slate-500">Bij uit is dit product altijd besteldbaar</p>
                </div>
                <Switch checked={productForm.track_stock} onCheckedChange={(checked) => setProductForm({ ...productForm, track_stock: checked })} />
              </div>
              {productForm.track_stock && (
                <div>
                  <Label htmlFor="product-stock">Voorraad</Label>
                  <Input id="product-stock" type="number" value={productForm.stock} onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })} />
                </div>
              )}
              <div>
                <Label htmlFor="product-image">Afbeelding-URL</Label>
                <Input id="product-image" value={productForm.image_url} onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="product-short-description">Korte omschrijving</Label>
                <Input id="product-short-description" value={productForm.short_description} onChange={(e) => setProductForm({ ...productForm, short_description: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="product-description">Omschrijving</Label>
                <Textarea id="product-description" value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="product-tags">Tags (komma-gescheiden)</Label>
                <Input id="product-tags" value={productForm.tags} onChange={(e) => setProductForm({ ...productForm, tags: e.target.value })} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" className="rounded-xl" onClick={() => setProductDialogOpen(false)}>
                  Annuleren
                </Button>
                <Button type="submit" className="rounded-xl bg-teal-600 hover:bg-teal-700">
                  {editingProduct ? 'Opslaan' : 'Product maken'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Category dialog */}
        <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingCategory ? 'Categorie bewerken' : 'Nieuwe categorie'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={saveCategory} className="space-y-4">
              <div>
                <Label htmlFor="category-name">Naam</Label>
                <Input id="category-name" required value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="category-description">Omschrijving</Label>
                <Textarea id="category-description" value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" className="rounded-xl" onClick={() => setCategoryDialogOpen(false)}>
                  Annuleren
                </Button>
                <Button type="submit" className="rounded-xl bg-teal-600 hover:bg-teal-700">
                  {editingCategory ? 'Opslaan' : 'Categorie maken'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CommercePage;
